"use client";

import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import Carousel1 from "@/components/organisms/carousels/Carousel1";
import {
  carouselClick,
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  videoAction,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { hexToRgba } from "@/utils/utils";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

interface BannerSlide {
  id?: number | string;
  meta_desktop_image?: string;
  meta_mobile_image?: string;
  meta_alt_image?: string;
  meta_priority?: number;
  meta_status?: boolean;
  meta_click_action?: "lead_drop" | "video";
  meta_video_link?: string;
  meta_redirect_url?: string;
}

const MAX_BANNERS = 4;
const DEFAULT_SLIDE_FREQUENCY = 5000;

const BannerVideo1 = (props: any) => {
  const { data: sectionData } = props;
  const dispatch = useDispatch();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const bannerRef = useRef<HTMLElement>(null);

  const {
    metadata = [],
    desktop_background_color = "#FFFFFF",
    mobile_background_color = "#FFFFFF",
    backvariant = "variant_1",
    gradient_color_1 = "rgba(0, 212, 235, 0.33)",
    gradient_color_2 = "rgba(0, 0, 0, 0.66)",
    gradient_color_3 = "rgba(0, 0, 0, 0.66)",
    gradient_color_4 = "rgba(83, 69, 195, 0.46)",
    slide_frequency = DEFAULT_SLIDE_FREQUENCY,
    status = true,
    enable_animation,
  } = sectionData || {};

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const activeSlides: BannerSlide[] = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: BannerSlide) => item.meta_status)
      .sort(
        (a: BannerSlide, b: BannerSlide) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0),
      )
      .slice(0, MAX_BANNERS);
  }, [metadata]);

  const currentSlide = activeSlides[currentSlideIndex];

  useEffect(() => {
    if (activeSlides.length > 0 && currentSlideIndex < activeSlides.length) {
      carouselClick({
        section: "banner_video_1",
        label: `banner_slide_${currentSlideIndex + 1}`,
      });
    }
  }, [currentSlideIndex, activeSlides]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (bannerRef.current && status) {
  //     FireWidgetViewedEvent(bannerRef, "banner_video_1");
  //   }
  // }, [status]);

  const handleBannerClick = useCallback(() => {
    if (!currentSlide) return;

    const redirectUrl = currentSlide.meta_redirect_url?.trim();

    if (
      currentSlide.meta_click_action === "video" &&
      currentSlide.meta_video_link
    ) {
      videoAction({
        section: "banner_video_1",
        label: currentSlide.meta_video_link,
      });
      setActiveVideoUrl(currentSlide.meta_video_link);
      setVideoOpen(true);
    } else if (redirectUrl) {
      ctaClick({
        section: "banner_video_1",
        component: "banner_click",
        label: "redirect",
        step: "1",
        entity: "banner_video_1",
        landing_url: redirectUrl,
      });
      if (typeof window !== "undefined") {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      }
    } else {
      ctaClick({
        section: "banner_video_1",
        component: "banner_click",
        label: "lead_drop",
        step: "1",
        entity: "banner_video_1",
      });
      dispatch(
        handleOpenLeaddrop({
          variant: LEAD_DROP_VARIANTS.REGISTER,
        }),
      );
    }
  }, [currentSlide, dispatch]);

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
      case "variant_3":
        return "none";
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
    }
  };

  if (!status || activeSlides.length === 0) {
    return null;
  }

  return (
    <>
      <section
        ref={bannerRef}
        className={cn(
          "banner-video-section w-full relative overflow-hidden",
        )}
        style={{ backgroundColor: desktop_background_color }}
      >
        <style>{`
          @media (max-width: 639px) {
            .banner-video-section { background-color: ${mobile_background_color} !important; }
          }
          @media (max-width: 767px) {
            .banner-video-dots { display: none !important; }
          }
        `}</style>

        <div
          className="absolute inset-0 z-0"
          style={{ background: getGradientBackground() }}
        />

        <div
          className={cn(
            "relative z-10 w-full mx-auto flex flex-col items-center",
            "px-[24px] py-0 gap-[8px]",
            "sm:px-[60px] sm:py-[40px] sm:gap-[30px] sm:max-w-[1080px]",
            "md:max-w-[1230px]",
            "lg:px-[80px] lg:max-w-[1430px]",
            "xl:px-[140px] xl:py-[80px] xl:max-w-[1750px]",
          )}
        >
          <RevealOnScroll
            className="w-full h-full"
            scaleStart={0.5}
            duration={1500}
            enableAnimation={isAnimationEnabled}
          >

          <div
            onClick={handleBannerClick}
            className={cn(
              "relative overflow-hidden cursor-pointer group",
              "w-full h-[860px] rounded-[24px] border border-[#00d4eb] mx-auto",
              "sm:max-w-[959px] sm:h-[398px] sm:rounded-[8px] sm:border-0",
              "md:max-w-full md:h-[460px]",
              "lg:max-w-[1270px] lg:h-[527px]",
              "xl:max-w-full xl:aspect-2560/1062 xl:h-auto",
            )}
            role="button"
            data-cta-name="banner_click"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBannerClick();
              }
            }}
          >
            {activeSlides.length === 1 ? (
              <div className="relative w-full h-full">
                {(activeSlides[0].meta_mobile_image ||
                  activeSlides[0].meta_desktop_image) && (
                  <>
                    <Image
                      src={
                        activeSlides[0].meta_mobile_image ||
                        activeSlides[0].meta_desktop_image!
                      }
                      alt={activeSlides[0].meta_alt_image || "Banner"}
                      fill
                      className="object-cover sm:hidden"
                      priority
                    />
                    <Image
                      src={
                        activeSlides[0].meta_desktop_image ||
                        activeSlides[0].meta_mobile_image!
                      }
                      alt={activeSlides[0].meta_alt_image || "Banner"}
                      fill
                      className="object-cover hidden sm:block"
                      priority
                    />
                  </>
                )}
              </div>
            ) : (
              <div
                className={cn(
                  "overflow-hidden",
                  "rounded-[24px]",
                  "sm:rounded-[8px] sm:bg-black",
                )}
              >
                <Carousel1
                  autoplayDelay={slide_frequency}
                  options={{ loop: true, duration: 45 }}
                  onSlideChange={(index) => setCurrentSlideIndex(index)}
                  navigationButtonClassName="hidden"
                >
                  {activeSlides.map((slide, index) => (
                    <div
                      key={slide.id || index}
                      className={cn(
                        "relative w-full",
                        "h-[860px]",
                        "sm:h-[398px] sm:px-[6px]",
                        "md:h-[460px]",
                        "lg:h-[527px]",
                        "xl:aspect-2560/1062 xl:h-auto",
                      )}
                    >
                      <div className="relative w-full h-full rounded-[10px] overflow-hidden">
                        <Image
                          src={
                            slide.meta_mobile_image ||
                            slide.meta_desktop_image ||
                            ""
                          }
                          alt={slide.meta_alt_image || `Banner ${index + 1}`}
                          fill
                          className="object-cover sm:hidden"
                          priority={index === 0}
                          fetchPriority={index === 0 ? "high" : "auto"}
                        />
                        <Image
                          src={
                            slide.meta_desktop_image ||
                            slide.meta_mobile_image ||
                            ""
                          }
                          alt={slide.meta_alt_image || `Banner ${index + 1}`}
                          fill
                          className="object-cover hidden sm:block"
                          priority={index === 0}
                          fetchPriority={index === 0 ? "high" : "auto"}
                        />
                      </div>
                    </div>
                  ))}
                </Carousel1>
              </div>
            )}
          </div>
          </RevealOnScroll>

          {activeSlides.length > 1 && (
            <div className="banner-video-dots flex gap-[6px] items-center h-[8px]">
              {activeSlides.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-[15px] shrink-0 transition-all duration-300",
                    currentSlideIndex === index
                      ? "w-[37.5px] h-[7.5px] bg-[#c8faff]"
                      : "w-[7.5px] h-[7.5px] bg-white",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {activeVideoUrl && (
        <YouTubeVideoOverlay
          isOpen={videoOpen}
          onClose={() => {
            setVideoOpen(false);
            setActiveVideoUrl("");
          }}
          videoUrl={activeVideoUrl}
        />
      )}
    </>
  );
};

export default BannerVideo1;
